import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataCard } from "../common/cards";
import { useAuth, useFake } from "../../hooks";
import { fetchProfilePicture } from "../../api/profile";
import { useQuery } from "@tanstack/react-query";
import LanguageCard from "./LanguageCard";
import TrendsCard from "./TrendsCard";
import DisconnectIcon from "../../plugin/components/common/icons/DisconnectIcon";

/**
 * Renders the operator view card
 * 
 * @param {Object} props - The props of the component
 * @returns The operator view card
 */
const OperatorViewCard = (props) => {
  const { signOut, user, fetchBearer } = useAuth();
  const { toggleRipple, isRippleEnabled } = useFake();
  const { t } = useTranslation();
  const [bearer, setBearer] = useState(null)

  const profilePictureQueryFunction = useCallback(
    async () => {
      return fetchProfilePicture(bearer)
    },
    [bearer],
  );

  const profilePictureQuery = useQuery({
    queryKey: ["profilePicture"],
    queryFn: profilePictureQueryFunction,
    enabled: !!bearer,
  });

  const profilePicture = useMemo(
    () => profilePictureQuery?.data?.url,
    [profilePictureQuery?.data],
  );

  const userLoginParts = useMemo(() => user?.signInDetails?.loginId?.split("."), [user]);

  const userInitials = useMemo(() => {
    return ((userLoginParts?.[0]?.[0] || "") + (userLoginParts?.[1]?.[0] || "")).toUpperCase();
  }, [userLoginParts]);

  const userName = useMemo(() => {
    return ((userInitials?.[0] + userLoginParts?.[0]?.slice(1) || "") + " " + (userInitials?.[1] + userLoginParts?.[1]?.slice(1) || ""));
  }, [userLoginParts, userInitials]);

  useEffect(() => {
    const updateBearer = async () => {
      setBearer(await fetchBearer())
    }
    updateBearer()
  }, [fetchBearer])

  return (
    <DataCard {...props}>
      <VStack alignItems={"center"} gap={"8px"} position={"relative"}>
        <Box position={"absolute"}
          display={"flex"}
          flexDirection={"column"}
          width={"full"}
          top={0}>
          <Box
            alignSelf={"center"}
            backgroundColor={"#70E000"}
            width={"15px"}
            height={"15px"}
            borderRadius={"full"}
            marginLeft={"64px"}
            marginTop={"4px"}
          />
        </Box>
        <Box padding={"2px"} bgGradient={"linear(45deg, #70E000, #FFFFFF)"} borderRadius={"full"}>
          {
            profilePicture ? (
              <Image
                alt={"operator"}
                borderRadius={"full"}
                boxSize={24}
                src={profilePicture}
              />
            ) : (
              <Box backgroundColor={"gray.300"}
                borderRadius={"full"}
                boxSize={24}
                display={"flex"}
                justifyContent={"center"}>
                <Heading alignSelf={"center"}
                  fontSize={"48px"}
                  fontWeight={"normal"}>
                  {userInitials}
                </Heading>
              </Box>
            )
          }
        </Box>
        <Text fontWeight={400} fontSize={"16px"} marginBottom={"8px"}>{userName}</Text>
        <LanguageCard />
        <TrendsCard />
        <Button
          alignSelf={"stretch"}
          onClick={signOut}
          size={"sm"}
          marginTop={"8px"}
          marginX={"16px"}
          fontWeight={400}
          variant={"primary"}
          fontSize={"14px"}
          leftIcon={<DisconnectIcon />}
        >
          {t("operator.logout")}
        </Button>
        <HStack marginTop={"8px"}>
          <FormControl alignItems={"center"}>
            <HStack>
              <FormLabel htmlFor="demo-click" mb={0}>Demo (click)</FormLabel>
              <Switch id="demo-click" value={isRippleEnabled} onChange={toggleRipple} />
            </HStack>
          </FormControl>
        </HStack>
      </VStack>
    </DataCard>
  );
};

export default React.memo(OperatorViewCard);
